import type { Bundle } from '../types'

/** Modèles de recherche : marques surveillées, autorisations marchand × marque. */
export const brands = {
  'brands.title': { fr: 'Modèles de recherche', en: 'Search templates' },
  'brands.subtitle': {
    fr: 'Configurez les marques, termes et domaines à surveiller sur crt.sh, les moteurs de recherche et le DNS.',
    en: 'Configure the brands, terms and domains to watch on crt.sh, search engines and DNS.',
  },
  'brands.importAuths': { fr: 'Importer des autorisations', en: 'Import authorisations' },
  'brands.newBrand': { fr: '+ Nouvelle marque', en: '+ New brand' },
  'brands.empty': { fr: 'Aucune marque configurée.', en: 'No brand configured.' },
  'brands.emptyHint': {
    fr: 'Aucune marque configurée. Cliquez sur « Nouvelle marque » pour commencer.',
    en: 'No brand configured. Use “New brand” to get started.',
  },
  'brands.disabled': { fr: 'Désactivé', en: 'Disabled' },
  'brands.searchTerms': { fr: 'Termes de recherche', en: 'Search terms' },
  'brands.customTyposquats': { fr: 'Typosquats personnalisés', en: 'Custom typosquats' },
  'brands.paymentSignatures': { fr: 'Signatures de paiement', en: 'Payment signatures' },
  'brands.officialDomains': { fr: 'Domaines officiels (exclus)', en: 'Official domains (excluded)' },
  'brands.webPatterns': { fr: 'Formulations de recherche web', en: 'Web search phrasings' },
  'brands.targetCountries': { fr: 'Pays cibles', en: 'Target countries' },
  'brands.scanningFor': {
    fr: 'Interrogation de crt.sh en cours pour les termes : {terms}…',
    en: 'Querying crt.sh for the terms: {terms}…',
  },
  'brands.lastScan': { fr: 'Dernier scan : {date}', en: 'Last scan: {date}' },
  'brands.scanBtn': { fr: 'Scanner', en: 'Scan' },
  'brands.scanning': { fr: 'Scan en cours…', en: 'Scan in progress…' },
  'brands.scanDisabledHint': {
    fr: 'Activez la marque pour lancer un scan',
    en: 'Enable the brand before running a scan',
  },
  'brands.scanHint': { fr: 'Lancer un scan crt.sh pour cette marque', en: 'Run a crt.sh scan for this brand' },
  'brands.scanFound': {
    fr: 'Scan terminé — {total} domaine(s) suspect(s) trouvé(s) pour « {name} » ({saved} enregistré(s))',
    en: 'Scan complete — {total} suspicious domain(s) found for “{name}” ({saved} saved)',
  },
  'brands.scanNone': {
    fr: 'Scan terminé — aucun nouveau certificat SSL suspect pour « {name} » sur crt.sh (dernières 24 h). Bonne nouvelle.',
    en: 'Scan complete — no new suspicious SSL certificate for “{name}” on crt.sh (last 24 h). Good news.',
  },
  'brands.scanError': { fr: 'Erreur lors du scan « {name} » : {error}', en: 'Scan of “{name}” failed: {error}' },
  'brands.enable': { fr: 'Activer', en: 'Enable' },
  'brands.disable': { fr: 'Désactiver', en: 'Disable' },
  'brands.deleteConfirm': {
    fr: 'Supprimer la marque « {name} » ? Cette action est irréversible.',
    en: 'Delete the brand “{name}”? This cannot be undone.',
  },

  // Catégories de marque
  'brands.category.INTERNAL': { fr: 'INTERNE', en: 'INTERNAL' },
  'brands.category.PARTNER': { fr: 'PARTENAIRE', en: 'PARTNER' },
  'brands.category.CLIENT': { fr: 'CLIENT', en: 'CLIENT' },

  // Marchands autorisés par marque
  'brands.authorizedMerchants': { fr: 'Marchands autorisés', en: 'Authorised merchants' },
  'brands.addMerchant': { fr: '+ Ajouter un marchand', en: '+ Add a merchant' },
  'brands.findMerchant': { fr: 'Rechercher un marchand', en: 'Find a merchant' },
  'brands.findMerchantPlaceholder': { fr: 'Nom ou URL du marchand…', en: 'Merchant name or URL…' },
  'brands.andMore': { fr: '… et {n} autres', en: '… and {n} more' },
  'brands.noMerchantMatch': {
    fr: 'Aucun marchand disponible ne correspond',
    en: 'No available merchant matches',
  },
  'brands.contractRef': { fr: 'Réf. contrat (facultatif)', en: 'Contract ref. (optional)' },
  'brands.contractRefShort': { fr: 'Réf. contrat', en: 'Contract ref.' },
  'brands.contractPlaceholder': { fr: 'Ex. : #IT-0847', en: 'E.g. #IT-0847' },
  'brands.authorize': { fr: 'Autoriser', en: 'Authorise' },
  'brands.authorizing': { fr: 'Ajout…', en: 'Adding…' },
  'brands.noAuthorized': {
    fr: 'Aucun marchand autorisé pour cette marque.',
    en: 'No merchant authorised for this brand.',
  },
  'brands.editAuth': { fr: 'Modifier le statut ou le contrat', en: 'Change status or contract' },
  'brands.revoke': { fr: 'Révoquer', en: 'Revoke' },
  'brands.revokeTitle': { fr: "Révoquer l'autorisation", en: 'Revoke the authorisation' },
  'brands.revokeConfirm': {
    fr: "Révoquer l'autorisation de « {merchant} » pour « {brand} » ?",
    en: 'Revoke “{merchant}”’s authorisation for “{brand}”?',
  },
  'brands.authStatus.ACTIVE': { fr: 'Actif', en: 'Active' },
  'brands.authStatus.SUSPENDED': { fr: 'Suspendu', en: 'Suspended' },
  'brands.authStatus.PENDING': { fr: 'En attente', en: 'Pending' },
  'brands.authStatus.REVOKED': { fr: 'Révoqué', en: 'Revoked' },

  // Formulaire de marque
  'brands.formEdit': { fr: 'Modifier « {name} »', en: 'Edit “{name}”' },
  'brands.formNew': { fr: 'Nouvelle marque', en: 'New brand' },
  'brands.formCreate': { fr: 'Créer la marque', en: 'Create brand' },
  'brands.formSaving': { fr: 'Sauvegarde…', en: 'Saving…' },
  'brands.fieldName': { fr: 'Nom de la marque *', en: 'Brand name *' },
  'brands.namePlaceholder': { fr: 'Ex. : SafaPay', en: 'E.g. SafaPay' },
  'brands.fieldCategory': { fr: 'Catégorie', en: 'Category' },
  'brands.catInternal': { fr: 'Interne (InTouch)', en: 'Internal (InTouch)' },
  'brands.catPartner': { fr: 'Partenaire (opérateur)', en: 'Partner (operator)' },
  'brands.catClient': { fr: 'Client tiers', en: 'Third-party client' },
  'brands.fieldColor': { fr: 'Couleur', en: 'Colour' },
  'brands.fieldDescription': { fr: 'Description', en: 'Description' },
  'brands.descriptionPlaceholder': { fr: 'Facultatif', en: 'Optional' },
  'brands.fieldTerms': {
    fr: 'Termes de recherche * (séparés par des virgules)',
    en: 'Search terms * (comma-separated)',
  },
  'brands.termsPlaceholder': { fr: 'Ex. : safapay, safa-pay, safapayment', en: 'E.g. safapay, safa-pay, safapayment' },
  'brands.termsHint': {
    fr: 'Sous-chaînes recherchées dans les noms de domaine. Trois caractères minimum par terme.',
    en: 'Substrings searched inside domain names. Three characters minimum per term.',
  },
  'brands.fieldOfficial': {
    fr: 'Domaines officiels à exclure (séparés par des virgules)',
    en: 'Official domains to exclude (comma-separated)',
  },
  'brands.officialPlaceholder': { fr: 'Ex. : safapay.com, safapay.sn', en: 'E.g. safapay.com, safapay.sn' },
  'brands.officialHint': {
    fr: "Les domaines légitimes de cette marque — ils ne déclencheront pas d'alerte.",
    en: 'This brand’s legitimate domains — they will never raise an alert.',
  },
  'brands.fieldTyposquats': {
    fr: 'Typosquats personnalisés (facultatif, séparés par des virgules)',
    en: 'Custom typosquats (optional, comma-separated)',
  },
  'brands.typosquatsPlaceholder': { fr: 'Ex. : safaapay, safpay, s4fapay', en: 'E.g. safaapay, safpay, s4fapay' },
  'brands.typosquatsHint': {
    fr: 'En plus des variantes générées automatiquement. Utile pour des fautes de frappe déjà observées.',
    en: 'In addition to the variants generated automatically. Useful for typos already seen.',
  },
  'brands.fieldSignatures': {
    fr: 'Signatures de paiement (séparées par des virgules)',
    en: 'Payment signatures (comma-separated)',
  },
  'brands.signaturesPlaceholder': {
    fr: 'Ex. : pay.wave.com, wave-payment-btn, checkout.wave.com',
    en: 'E.g. pay.wave.com, wave-payment-btn, checkout.wave.com',
  },
  'brands.signaturesHint': {
    fr: "URLs, widgets ou boutons de paiement à détecter sur les sites tiers. Utilisés par le scan d'intégrations.",
    en: 'Payment URLs, widgets or buttons to detect on third-party sites. Used by the integration scan.',
  },
  'brands.fieldPatterns': {
    fr: 'Formulations de recherche web — marchands non déclarés (une par ligne)',
    en: 'Web search phrasings — undeclared merchants (one per line)',
  },
  'brands.patternsHint': {
    fr: "{brandToken} est remplacé par le nom de la marque, {countryToken} par le nom du pays scanné. Une formulation avec {countryToken} n'est utilisée que lors des scans par pays, pas lors du scan mondial. Chaque formulation ajoute une recherche par pays scanné.",
    en: '{brandToken} is replaced by the brand name and {countryToken} by the scanned country. A phrasing containing {countryToken} is used only during country scans, never during the worldwide scan. Each phrasing adds one search per scanned country.',
  },
  'brands.addCountryPatterns': {
    fr: '+ Ajouter des formulations par pays',
    en: '+ Add country-specific phrasings',
  },
  'brands.patternsPreview': { fr: 'Aperçu pour le Sénégal : {examples}', en: 'Preview for Senegal: {examples}' },
  'brands.patternsPreviewBrand': { fr: 'Marque', en: 'Brand' },
  'brands.patternsPreviewCountry': { fr: 'Sénégal', en: 'Senegal' },
  'brands.fieldCountries': {
    fr: 'Pays cibles (codes ISO, facultatif)',
    en: 'Target countries (ISO codes, optional)',
  },
  'brands.countriesPlaceholder': { fr: 'Ex. : SN, CI, ML (vide = mondial)', en: 'E.g. SN, CI, ML (empty = worldwide)' },
  'brands.nameRequired': { fr: 'Le nom est obligatoire', en: 'Name is required' },
  'brands.termsRequired': {
    fr: 'Au moins un terme de recherche est requis',
    en: 'At least one search term is required',
  },
  'brands.saveError': { fr: "Erreur lors de l'enregistrement", en: 'Save failed' },

  // Import des autorisations
  'authImport.title': {
    fr: 'Importer des autorisations marchand × marque',
    en: 'Import merchant × brand authorisations',
  },
  'authImport.help': {
    fr: 'Colonnes attendues : <merchant>, <brand>, et facultativement <status> et <contractRef>. Le marchand se désigne par son domaine, son URL ou son nom exact — le domaine est le plus sûr quand deux marchands portent le même nom.',
    en: 'Expected columns: <merchant>, <brand>, and optionally <status> and <contractRef>. Identify the merchant by domain, URL or exact name — the domain is safest when two merchants share a name.',
  },
  'authImport.chooseFile': { fr: 'Choisir un fichier CSV', en: 'Choose a CSV file' },
  'authImport.dropHint': { fr: 'ou déposez le fichier ici', en: 'or drop the file here' },
  'authImport.fileInfo': { fr: '{file} — {n} ligne(s)', en: '{file} — {n} row(s)' },
  'authImport.emptyFile': { fr: 'Fichier vide.', en: 'Empty file.' },
  'authImport.tooShort': {
    fr: 'Le fichier doit contenir un en-tête et au moins une ligne.',
    en: 'The file must contain a header row and at least one data row.',
  },
  'authImport.missingCols': {
    fr: 'Colonnes obligatoires manquantes. Attendu : « merchant » (ou marchand, url, domaine) et « brand » (ou marque).',
    en: 'Required columns missing. Expected: “merchant” (or url, domain) and “brand”.',
  },
  'authImport.noUsableRow': {
    fr: 'Aucune ligne exploitable après l’en-tête.',
    en: 'No usable row after the header.',
  },
  'authImport.readError': { fr: 'Lecture du fichier impossible.', en: 'The file could not be read.' },
  'authImport.simulate': { fr: 'Simuler l’import', en: 'Simulate the import' },
  'authImport.analysing': { fr: 'Analyse…', en: 'Analysing…' },
  'authImport.apply': { fr: 'Appliquer ({n} ligne(s))', en: 'Apply ({n} row(s))' },
  'authImport.previewTitle': { fr: 'Simulation — rien n’a été enregistré', en: 'Simulation — nothing was saved' },
  'authImport.toCreate': { fr: 'à créer', en: 'to create' },
  'authImport.toUpdate': { fr: 'à mettre à jour', en: 'to update' },
  'authImport.upToDate': { fr: 'déjà à jour', en: 'already up to date' },
  'authImport.rejected': { fr: 'rejetée(s)', en: 'rejected' },
  'authImport.colRow': { fr: 'Ligne', en: 'Row' },
  'authImport.colContent': { fr: 'Contenu', en: 'Content' },
  'authImport.colReason': { fr: 'Motif du rejet', en: 'Reason for rejection' },
  'authImport.moreErrors': { fr: '… et {n} autre(s).', en: '… and {n} more.' },
  'authImport.doneTitle': { fr: 'Import appliqué', en: 'Import applied' },
  'authImport.doneMsg': {
    fr: '{created} autorisation(s) créée(s), {updated} mise(s) à jour, {unchanged} inchangée(s).',
    en: '{created} authorisation(s) created, {updated} updated, {unchanged} unchanged.',
  },
  'authImport.doneRejected': { fr: ' {n} ligne(s) rejetée(s).', en: ' {n} row(s) rejected.' },
  'authImport.another': { fr: 'Importer un autre fichier', en: 'Import another file' },
} satisfies Bundle
